import React, { useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useGetMyThreadsQuery, useToggleSaveThreadMutation } from '@/store/api/corkBoard/corkBoardApi';
import { useUserThreadsList } from '@/hooks/residents/corkBoard/useUserThreadsList';
import { useCorkBoardMutation } from '@/hooks/residents/corkBoard/useCorkBoardMutation';
import { useThreadActions } from '@/hooks/residents/corkBoard/useThreadActions';
import { useToast } from '@/contexts/admin/ToastContext';
import { NewsfeedUserListScreen } from '@/components/residents/redesign';
import type { CorkBoardThreadSummary } from '@/types/residents/corkBoard';

type MyThreadsNavigationProp = StackNavigationProp<RootStackParamList>;

export const MyThreadsScreen: React.FC = () => {
  const navigation = useNavigation<MyThreadsNavigationProp>();
  const { showSuccess, showError } = useToast();
  const [toggleSaveTrigger] = useToggleSaveThreadMutation();
  const { execute: executeToggleSave } = useCorkBoardMutation<string, any>(
    toggleSaveTrigger, { errorFallback: 'Failed to update bookmark.' },
  );

  const {
    threads,
    isInitialLoad,
    isRefreshing,
    isLoadingMore,
    isError,
    handleRefresh,
    handleEndReached,
  } = useUserThreadsList({ queryHook: useGetMyThreadsQuery, variant: 'my' });

  const {
    handleEditThread,
    openDeleteConfirm,
    openReportModal,
    threadActionModals,
  } = useThreadActions();

  const handleThreadPress = useCallback((thread: CorkBoardThreadSummary) => {
    navigation.navigate('ThreadDetail', { threadId: thread.id });
  }, [navigation]);

  const handleCreatePost = useCallback(() => {
    navigation.navigate('CreateThread');
  }, [navigation]);

  const handleSaveThread = useCallback(async (thread: CorkBoardThreadSummary) => {
    const result = await executeToggleSave(thread.id, { silent: true });
    if (result.success) {
      const saved = result.data?.saved ?? !thread.isSaved;
      showSuccess(saved ? 'Thread saved' : 'Thread unsaved');
    } else {
      showError('Failed to save thread');
    }
  }, [executeToggleSave, showSuccess, showError]);

  const handleBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('CommunityBoard');
    }
  }, [navigation]);

  return (
    <>
      <NewsfeedUserListScreen
        title="My Posts"
        subtitle="Posts you've created"
        screenName="MyThreads"
        threads={threads}
        isInitialLoad={isInitialLoad}
        isRefreshing={isRefreshing}
        isLoadingMore={isLoadingMore}
        isError={isError}
        onRefresh={handleRefresh}
        onEndReached={handleEndReached}
        onThreadPress={handleThreadPress}
        onBack={handleBack}
        emptyStateVariant="myThreads"
        onCreatePost={handleCreatePost}
        onSave={handleSaveThread}
        onEdit={handleEditThread}
        onDelete={openDeleteConfirm}
        onReport={openReportModal}
      />
      {threadActionModals}
    </>
  );
};
